import { toast } from "react-toastify";
import { SUBDOMAIN } from "../../assets/meta/meta";
import { BACKEND_HOST } from "../../api";
import { requestPaymentNicePay } from "./nicepay/api";
import { getHectoPayParams, requestPaymentHecto } from "./hecto/api";
import type { PGModule } from "./usePGModules";

interface PaymentUrls {
  /** Toss successUrl — full backend URL */
  successUrl: string;
  /** Toss failUrl — full frontend URL */
  failUrl: string;
  /** NicePay returnUrl — backend path only (e.g. '/package/order-nice/') */
  niceReturnUrl: string;
  /** Hecto notiUrl — full backend URL */
  hectoNotiUrl: string;
  /** Hecto nextUrl — full backend URL */
  hectoNextUrl: string;
  /** Hecto cancUrl — full backend URL */
  hectoCancUrl: string;
}

interface DispatchPaymentArgs {
  selectedPG: PGModule;
  paymentWidgetRef: React.MutableRefObject<any>;
  orderId: string;
  orderName: string;
  amount: number;
  userInfo: {
    username?: string;
    name?: string;
    phone_num?: string;
    id?: string | number;
    email?: string;
  };
  urls: PaymentUrls;
  taxFreeAmount?: number;
  /** NicePay/Hecto 실패 시 이동할 프론트 경로 */
  failNavigate?: () => void;
}

interface DispatchPaymentResult {
  redirectRequested: boolean;
}

/**
 * 선택된 PG에 따라 결제를 실행합니다.
 * Toss / NicePay / Hecto 3-way 디스패치를 하나로 통합합니다.
 */
export async function dispatchPayment({
  selectedPG,
  paymentWidgetRef,
  orderId,
  orderName,
  amount,
  userInfo,
  urls,
  taxFreeAmount,
  failNavigate,
}: DispatchPaymentArgs): Promise<DispatchPaymentResult> {
  if (selectedPG.code === "toss") {
    const paymentWidget = paymentWidgetRef.current;
    if (!paymentWidget || typeof paymentWidget.requestPayment !== "function") {
      toast.error("토스 결제 위젯이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.");
      failNavigate?.();
      return { redirectRequested: false };
    }

    const tossArgs: Record<string, any> = {
      orderId,
      orderName,
      customerName: userInfo.username || userInfo.name,
      customerMobilePhone: userInfo.phone_num,
      successUrl: urls.successUrl,
      failUrl: urls.failUrl,
    };
    if (taxFreeAmount != null) {
      tossArgs.taxFreeAmount = taxFreeAmount;
    }

    try {
      await paymentWidget.requestPayment(tossArgs);
      return { redirectRequested: true };
    } catch (error: any) {
      if (error?.code === "USER_CANCEL") {
        return { redirectRequested: false };
      }
      console.log("toss requestPayment error", error);
      toast.error("결제에 실패하였습니다.");
      failNavigate?.();
      return { redirectRequested: false };
    }
  } else if (selectedPG.code === "nicepay") {
    requestPaymentNicePay({
      orderId,
      amount,
      goodsName: orderName,
      method: "card",
      returnUrl: urls.niceReturnUrl,
      errorCb: (result: any) => {
        console.log("nicepay errorCb", result);
        toast.error("결제에 실패하였습니다.");
        failNavigate?.();
      },
    });
    return { redirectRequested: true };
  } else if (selectedPG.code === "hecto") {
    const encryptedParams = await getHectoPayParams({
      orderId,
      amount,
      phone: userInfo.phone_num || "",
      user_id: String(userInfo.id || ""),
      email: userInfo.email,
      username: userInfo.username || userInfo.name,
    });

    requestPaymentHecto({
      ...encryptedParams,
      pktHash: encryptedParams.pktHash ?? encryptedParams.hashCipher,
      mchtName: "어메이징캠프",
      mchtEName: "AmazingCamp",
      mchtParam: SUBDOMAIN,
      pmtPrdtNm: orderName,
      notiUrl: urls.hectoNotiUrl,
      nextUrl: urls.hectoNextUrl,
      cancUrl: urls.hectoCancUrl,
    });
    return { redirectRequested: true };
  } else {
    toast.error("결제모듈이 설정되지 않았습니다.");
    return { redirectRequested: false };
  }
}
